/**
 * PendingReviewsWidget
 *
 * Dashboard homepage widget showing pending content reviews.
 * Displays count badge and up to 5 pending items sorted by creation date.
 *
 * Part of Story 29: Content Review Dashboard with Reviewer Attribution
 *
 * Date: December 16, 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ClipboardList,
  FileText,
  Tag,
  FolderTree,
  ArrowRight,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';
import { getPendingItems, PendingItem } from '@/services/contentReviewService';

interface PendingReviewsWidgetProps {
  onItemClick?: (item: PendingItem) => void;
}

export function PendingReviewsWidget({ onItemClick }: PendingReviewsWidgetProps) {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [data, setData] = useState<{
    totalPending: number;
    items: PendingItem[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingItems();
  }, [selectedSite?.id]);

  const fetchPendingItems = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getPendingItems(selectedSite?.id || null, 5);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Failed to load pending reviews');
      }
    } catch (err) {
      console.error('[PendingReviewsWidget] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'ai_article':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'product_aeo':
        return <Tag className="h-4 w-4 text-green-500" />;
      case 'category_aeo':
        return <FolderTree className="h-4 w-4 text-purple-500" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const handleItemClick = (item: PendingItem) => {
    if (onItemClick) {
      onItemClick(item);
    } else {
      navigate(`/content-review?id=${item.id}&type=${item.contentType}`);
    }
  };

  const handleViewAll = () => {
    navigate('/content-review');
  };

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Pending Content Reviews
          </CardTitle>
          <Badge variant="secondary">Error</Badge>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchPendingItems}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <ClipboardList className="h-4 w-4" />
          Pending Content Reviews
        </CardTitle>
        {loading ? (
          <Skeleton className="h-5 w-8" />
        ) : (
          <Badge variant={data?.totalPending ? 'destructive' : 'secondary'}>
            {data?.totalPending || 0}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        ) : data?.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500 mb-2" />
            <p className="text-sm text-muted-foreground">
              All caught up! No pending reviews.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {data?.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer transition-colors"
                onClick={() => handleItemClick(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleItemClick(item);
                  }
                }}
              >
                {getContentTypeIcon(item.contentType)}
                <span className="flex-1 text-sm truncate" title={item.title}>
                  {item.title}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                </span>
              </div>
            ))}
          </div>
        )}

        <Button
          variant="ghost"
          className="w-full mt-3"
          onClick={handleViewAll}
        >
          View All
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default PendingReviewsWidget;
